import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';

interface ViewerCardProps {
  image: string;
  name: string;
  opponent: string;
  fightDate: string;
  points: number | null;
  isHighlighted?: boolean;
  isSelected?: boolean;
  hasSelectedFighter?: boolean;
  title: string;
  gender: string;
  onClick: () => void;
  isLoading?: boolean;
}

const ViewerCard: React.FC<ViewerCardProps> = ({
  image,
  name,
  opponent,
  fightDate,
  points,
  isHighlighted = false,
  isSelected = false,
  hasSelectedFighter = false,
  title,
  gender,
  onClick,
  isLoading = false,
}) => {
  return (
    <Box
      onClick={onClick}
      sx={{
        width: { xs: '101.5%', sm: '95%' },
        height: { xs: '101.5%', sm: '96%' },
        position: 'relative',
        bgcolor: isHighlighted ? 'rgba(20, 20, 20, 0.9)' : 'transparent',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: '8px',
        cursor: 'pointer',
        border: isSelected
          ? '3px solid white'
          : '1px solid rgba(255, 255, 255, 0.3)',
        transition: 'all 0.3s ease',
        opacity: hasSelectedFighter && !isSelected ? 0.6 : 1,
        '&:hover': {
          opacity: 1,
          border: isSelected ? '3px solid white' : '1px solid white',
        },
      }}
    >
      {/* Fighter Image Container */}
      <Box
        sx={{
          width: '100%',
          height: { xs: '55%', sm: '55%' },
          position: 'relative',
          marginBottom: { xs: '2px', sm: '4px' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Points Box */}
        <Box
          sx={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            padding: { xs: '0px 2px', sm: '2px 4px' },
            borderRadius: '4px',
            border: `1px solid ${points !== null ? '#8b0000' : 'rgba(255, 255, 255, 0.3)'}`,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            zIndex: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '0.5rem', sm: 'clamp(0.5rem, 0.8vw, 1.5rem)' },
              fontFamily: 'Rubik Mono One',
              color: points !== null ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
            }}
          >
            {points !== null ? points : 'N/A'}
          </Typography>
        </Box>

        {/* Title Crown Box */}
        {title === 'T' && (
          <Box
            sx={{
              position: 'absolute',
              top: '4px',
              left: '4px',
              padding: { xs: '1px 3px', sm: '2px 4px' },
              borderRadius: '4px',
              border: '1px solid gold',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '0.6rem', sm: 'clamp(0.5rem, 0.8vw, 1.5rem)' },
                color: 'gold',
              }}
            >
              👑
            </Typography>
          </Box>
        )}

        {isLoading ? (
          <CircularProgress
            size={20}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
            }}
          />
        ) : (
          <Box
            component="img"
            sx={{
              maxWidth: { xs: '65%', sm: '90%' },
              maxHeight: { xs: '65%', sm: '90%' },
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '4px',
            }}
            src={image}
            alt="Fighter Image"
          />
        )}
      </Box>

      {/* Fighter Info */}
      <Box
        sx={{
          width: '95%',
          height: { xs: '37%', sm: '40%' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: '1px', sm: '2px' },
        }}
      >
        <Typography
          sx={{
            color: 'white',
            fontSize: { xs: '0.65rem', sm: 'clamp(0.55rem, 0.9vw, 1.5rem)' },
            fontWeight: 'normal',
            fontFamily: 'Rubik Mono One',
            textAlign: 'center',
            lineHeight: 1.1,
          }}
        >
          {name}
        </Typography>
        <Typography
          sx={{
            color: '#cccccc',
            fontSize: { xs: '0.55rem', sm: 'clamp(0.45rem, 0.8vw, 1.5rem)' },
            fontFamily: 'Bebas Neue',
            textAlign: 'center',
            lineHeight: 1.1,
          }}
        >
          vs. {opponent}
        </Typography>
        <Typography
          sx={{
            color: '#cccccc',
            fontSize: { xs: '0.55rem', sm: 'clamp(0.45rem, 0.8vw, 1.5rem)' },
            fontFamily: 'Bebas Neue',
            textAlign: 'center',
            lineHeight: 1,
          }}
        >
          {fightDate}
        </Typography>
      </Box>
    </Box>
  );
};

export default ViewerCard;
